import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { ArticleContentTypeService } from 'src/app/content/services/article-content-type.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { WithContentBodyFormSection } from 'src/app/shared/mixins/with-content-body-form-section';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithStandfirstFormSection } from 'src/app/shared/mixins/with-standfirst-form-section';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { IArticleContentDto } from 'src/app/core/dtos/interfaces/i-article-content-dto';
import { ArticleContentDto } from 'src/app/core/dtos/article-content-dto';
import { WithPublishTimeFormSection } from 'src/app/shared/mixins/with-publish-time';
import { WithContentListFormSection } from 'src/app/shared/mixins/with-content-lists-form-section';
import { ContentListService } from 'src/app/content-list-management/shared/services/cmsapi/content-list.service';
import { IContentListDto } from 'src/app/core/dtos/interfaces/i-content-list-dto';
import { WithContentListFromWebsite } from 'src/app/shared/mixins/with-content-lists-from-websites';
import { ContentEditorComponent } from 'src/app/shared-form-sections/components/content-editor/content-editor.component';
import { ISmartLinkMentionFlashTarget } from 'src/app/shared-form-sections/components/smart-link-card/smart-link-card.component';
import { SmartLinkEvidenceField } from 'src/app/core/models/i-smart-link';
import { ClauseMissReason } from 'src/app/shared-form-sections/components/content-editor/body-mention-locator';
import { WithRelatedDataFormSection } from 'src/app/shared/mixins/with-related-data-form-section';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { IManager } from 'src/app/core/models/i-manager';
import { IFund } from 'src/app/core/models/i-fund';
import { ISector } from 'src/app/core/models/i-sector';
import { IAssetClass } from 'src/app/core/models/i-asset-class';
import { IEtf } from 'src/app/core/models/i-etf';
import { IInvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { IShare } from 'src/app/core/models/i-share';
import { IPrivateMarketsFund } from 'src/app/core/models/i-private-markets-fund';
import { WithAuthorsFormSection } from 'src/app/shared/mixins/with-authors-form-section';
import { AuthorService } from 'src/app/shared/services/author.service';
import { IAuthor } from 'src/app/core/models/i-author';
import { WithRelatedArticlesFormSection } from 'src/app/shared/mixins/with-related-articles-form-section';
import { IRelatedContent } from 'src/app/core/models/i-related-content';
import { IExternalLink } from 'src/app/core/models/i-external-link';
import { WithExternalLinksFormSection } from 'src/app/shared/mixins/with-external-links';
import { WithTaggingFormSection } from 'src/app/shared/mixins/with-tagging-form-section';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { WithContentVisibilityFormSection } from 'src/app/shared/mixins/with-visibility-section';
import { WithSponsorsFormSection } from 'src/app/shared/mixins/with-sponsors-form-section';
import { SponsorService } from 'src/app/shared/services/sponsor.service';
import { ISponsor } from 'src/app/core/models/i-sponsor';
import { WithColumnistContentFormSection } from 'src/app/shared/mixins/with-columnist-content-form-section';
import { WithMainVideoImageFormSection } from 'src/app/shared/mixins/with-main-video-image-section';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IRepurposeContentResponse } from 'src/app/core/dtos/interfaces/i-repurpose-content-response-dto';
import { ContentStateService } from 'src/app/content/services/content-state.service';
import { IErrorResponse } from 'src/app/core/dtos/interfaces/i-error-response';
import { SharedContentTypeService } from 'src/app/content/services/shared-content-type.service';
import { UrlService } from 'src/app/shared/services/url.service';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';
import { USER_ROLES } from 'src/app/core/constants/cms-user-roles';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { DataBlockType } from 'src/app/core/dtos/enums/data-block-type';
import { IPrivateMarketsFundBodyLinkAttempt } from 'src/app/core/dtos/interfaces/i-private-markets-body-link';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { WithSurtitlesFormSection } from 'src/app/shared/mixins/with-surtitles-form-section';
import { ITypedSelectListItem } from 'src/app/shared-form-sections/interfaces/i-typed-select-list-item';
import { ISurtitle } from 'src/app/core/models/i-surtitle';
import { SmartLinkArticleGateway } from 'src/app/shared/services/smart-linking-state.service';
import { AutoTagArticleGateway } from 'src/app/shared/services/auto-tagging-state.service';
import { FeatureFlagsService } from 'src/app/core/feature-flags/feature-flag.service';
import { Features } from 'src/app/core/feature-flags/features';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import {
  handleTagAddedAsSurtitle,
  handleTagRemoved,
  handleTagSelected,
} from 'src/app/shared/utils/tagging-handler';

class ArticleContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});
  formReady = new BehaviorSubject<boolean>(false);

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public contentListService: ContentListService,
    public managerService: ManagerService,
    public authorService: AuthorService,
    public sponsorService: SponsorService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {}
}

const ArticleContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithStandfirstFormSection(
        WithContentBodyFormSection(
          WithNextStateFormSection(
            WithLanguagesFormSection(
              WithPublishTimeFormSection(
                WithRelatedDataFormSection(
                  WithRelatedArticlesFormSection(
                    WithAuthorsFormSection(
                      WithContentVisibilityFormSection(
                        WithContentListFormSection(
                          WithTaggingFormSection(
                            WithSurtitlesFormSection(
                              WithContentListFromWebsite(
                                WithWebsitesFormSection(
                                  WithExternalLinksFormSection(
                                    WithSponsorsFormSection(
                                      WithColumnistContentFormSection(
                                        WithMainVideoImageFormSection(
                                          ArticleContentFormComponentBase
                                        )
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

// NOTE: Must not undercut the keyframes, which are this long and carry the fade themselves.
const MENTION_FLASH_MS = 3000;

const MENTION_FLASH_BODY_MISS_MESSAGES: Record<ClauseMissReason, string> = {
  absent:
    'That mention is no longer in the article body — it may have been edited ' +
    'since the suggestions were found.',
  split:
    'That mention\'s quote is split across paragraphs, so it can\'t be highlighted.',
};

// NOTE: SmartLinkEvidenceField is a union type, so the left column's order — which the page
// scroll picks its target from (ADR-0007) — has to be spelled out to be walked.
const FLASH_SURFACE_ORDER: SmartLinkEvidenceField[] = [
  'headline',
  'standfirst',
  'body',
];

// What one flash did: the surfaces that took a tint, and why a body target came back empty.
interface IMentionFlashOutcome {
  lit: SmartLinkEvidenceField[];
  bodyMiss: ClauseMissReason | null;
}

/**
 * True once less than half of a flashed surface is on screen.
 *
 * NOTE: A threshold rather than never moving, against a strict reading of PM-144's AC — the
 * argument and the reporter's sign-off are in ADR-0007.
 */
export function shouldBringEditorIntoView(
  rect: { top: number; bottom: number; height: number },
  viewportHeight: number
): boolean {
  const visible =
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  return visible < Math.min(rect.height, viewportHeight) / 2;
}

@Component({
  selector: 'app-article-content-form',
  templateUrl: './article-content-form.component.html',
  styleUrls: ['./article-content-form.component.scss'],
})
export class ArticleContentFormComponent
  extends ArticleContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  @ViewChild(ContentEditorComponent)
  contentEditorComponent: ContentEditorComponent;

  // NOTE: Read here rather than exposed by the editor, which knows nothing about the page.
  @ViewChild(ContentEditorComponent, { read: ElementRef })
  contentEditorHost: ElementRef<HTMLElement>;

  // The other two surfaces a flash can land on, so the page scroll can reach whichever of the
  // three sits topmost.
  @ViewChild('headlineSurface', { read: ElementRef })
  headlineHost: ElementRef<HTMLElement>;

  @ViewChild('standfirstSurface', { read: ElementRef })
  standfirstHost: ElementRef<HTMLElement>;

  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewArticle = false;
  isSmartLinkingEnabled = false;
  // NOTE: Plural since a card click lights every surface its suggestion cites (ADR-0007). Body
  // blocks are not in here — they carry their own attribute inside the editor.
  flashedFields: SmartLinkEvidenceField[] = [];
  isBodyLinkFormatsEnabled = false;
  isAutoTaggingEnabled = false;

  id: string;
  pageHeader: string;
  private mentionFlashTimer: number | null = null;
  articleContent$!: Observable<IArticleContentDto>;
  initialArticleContent!: IArticleContentDto;
  currentUserName: string;

  form = this.buildForm();

  smartLinkGateway: SmartLinkArticleGateway = {
    isSaving: () => this.isSaving,
    getContent: () => ({
      headline: this._buildHeadline() ?? '',
      standfirst: this._buildStandfirst() ?? '',
      body: this._buildBody() ?? '',
    }),
    getSavedAt: () =>
      this.initialArticleContent?.lastUpdatedDate
        ? new Date(this.initialArticleContent.lastUpdatedDate).toISOString()
        : null,
  };

  // AI Auto-Tagging (CMS-2565) needs the same save-before-run coordination. A run sends only
  // { trigger }; the content getters here are for the panel's content-changed check (comparing the
  // live edit buffer against the completed run's content hash), not for the run request.
  autoTagGateway: AutoTagArticleGateway = {
    isDirty: () => this.form.dirty,
    isSaving: () => this.isSaving,
    saveExisting$: () => this.saveExistingArticle$(),
    getContent: () => ({
      headline: this._buildHeadline() ?? '',
      standfirst: this._buildStandfirst() ?? '',
      body: this._buildBody() ?? '',
    }),
    contentChanges$: () => this.form.valueChanges,
  };

  canRepurpose$: Observable<boolean>;

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
      [this.FORM_FIELDS.STANDFIRST]: [''],
      [this.FORM_FIELDS.LANGUAGE]: [null],
      [this.FORM_FIELDS.CONTENT_BODY]: this.formBuilder.group(
        {
          [this.FORM_FIELDS.BODY]: [],
          [this.FORM_FIELDS.IMGS]: this.formBuilder.array([]),
          [this.FORM_FIELDS.VIDEOS]: this.formBuilder.array([]),
        },
        {
          validators: [this._initNoImgValidation()],
        }
      ),
      [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group(
        {},
        {
          validators: [this._initWebsiteValidation()],
        }
      ),
      [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
        [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
        [this.FORM_FIELDS.CONTENT_IDS]: [],
      }),
      [this.FORM_FIELDS.PUBLISH]: this.formBuilder.group(
        {
          [this.FORM_FIELDS.DATE]: [],
          [this.FORM_FIELDS.TIMEZONE]: [],
          [this.FORM_FIELDS.SHOW_PUBLISH_TIME]: [false],
        },
        {
          validators: [this._initPublishTimeValidation()],
        }
      ),
      [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
      [this.FORM_FIELDS.NEXT_STATES]: [],
      [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.SPONSORS]: this.formBuilder.group(
        {
          [this.FORM_FIELDS.SELECTED_SPONSOR]: [],
          [this.FORM_FIELDS.IS_SPONSORED]: [false],
        },
        {
          validators: [this._initSponsorValidation()],
        }
      ),
      [this.FORM_FIELDS.CONTENT_LISTS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_MANAGERS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_FUNDS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_SECTORS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ASSET_CLASSES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ETFS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_INVESTMENT_TRUSTS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_SHARES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_PRIVATE_MARKETS_FUNDS]: this.formBuilder.array(
        []
      ),
      [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ARTICLES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.COLUMNIST_CONTENT]: this.formBuilder.control(false),
      [this.FORM_FIELDS.EXTERNAL_LINKS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.SURTITLES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.EDITION_LANGUAGE]: ['en'],
      [this.FORM_FIELDS.TYPED_TAGS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TAGS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TOPICS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TOPICS_WITH_TRANSLATIONS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.COMPANIES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.PEOPLE]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RESTRICTED_TO_REGISTERED_USERS]: [null],
      [this.FORM_FIELDS.NOT_FOR_GOOGLE_NEWS_SITEMAPS]: [null],
      [this.FORM_FIELDS.NOT_FOR_NEWS_FEED]: [null],
      [this.FORM_FIELDS.HIDE_THIS_ARTICLE]: [null],
      [this.FORM_FIELDS.PAYWALL_PREMIUM_CONTENT]: [null],
      [this.FORM_FIELDS.NOT_FOR_COMPETITORS]: [null],
      [this.FORM_FIELDS.MAIN_IMAGE]: this.formBuilder.array<IImgMediaDto[]>([]),
      [this.FORM_FIELDS.MAIN_VIDEO]: this.formBuilder.array<IVideoMediaDto[]>(
        []
      ),
      [this.FORM_FIELDS.DISPLAY_IMAGE_ON_WEBSITE]:
        this.formBuilder.control<boolean>(false),
    });
  }

  readonly getContentFn$ = (id: string) => {
    return this.getArticleContent$(id);
  };

  readonly createNewVersionFn$ = (contentId, versionId) =>
    this.articleService.createVersion$(contentId, versionId);

  readonly deleteVersionFn$ = (contentId, versionId) =>
    this.articleService.deleteVersion$(contentId, versionId);

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public articleService: ArticleContentTypeService,
    public sharedService: SharedContentTypeService,
    public _dialogService: DialogService,
    public authorService: AuthorService,
    public sponsorService: SponsorService,
    public contentListService: ContentListService,
    public managerService: ManagerService,
    public signalRService: SignalrService,
    public topicService: TopicService,
    public urlService: UrlService,
    public userRolesService: UserRolesService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService,
    private _contentStateService: ContentStateService,
    private _featureFlagsService: FeatureFlagsService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {
    super(
      languageService,
      deliveryTargetsService,
      formBuilder,
      _dialogService,
      contentListService,
      managerService,
      authorService,
      sponsorService,
      signalRService,
      topicService
    );

    // NOTE: these flags are cms-api email allowlists (/api/features). currentUser is a BehaviorSubject,
    // so forkJoin of these sources still resolves synchronously here, before the child panels read it in ngOnInit.
    forkJoin({
      isSmartLinkingEnabled:
        this._featureFlagsService.isFeatureEnabledForUserByEmail$(
          Features.SMART_LINKING
        ),
      isBodyLinkFormatsEnabled:
        this._featureFlagsService.isFeatureEnabledForUserByEmail$(
          Features.BODY_LINK_FORMATS
        ),
      isAutoTaggingEnabled:
        this._featureFlagsService.isFeatureEnabledForUserByEmail$(
          Features.SMART_TAGGING
        ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ({
          isSmartLinkingEnabled,
          isBodyLinkFormatsEnabled,
          isAutoTaggingEnabled,
        }) => {
          this.isSmartLinkingEnabled = isSmartLinkingEnabled;
          this.isBodyLinkFormatsEnabled = isBodyLinkFormatsEnabled;
          this.isAutoTaggingEnabled = isAutoTaggingEnabled;
        }
      );
  }

  ngOnInit(): void {
    this._routerUrlSub();
    this.canRepurpose$ = this.userRolesService.isUserInRole(
      USER_ROLES.CanRepurposeArticles
    );

    if (this.isNewArticle) {
      this.pageHeader = 'Create Article';
      this.initialArticleContent = new ArticleContentDto({});
      this.initialArticleContent.dynamicData.allowableNextStates = [];
      this.initialArticleContent.dynamicData.versions = [];
      this.initialArticleContent.dynamicData.liveUrls = [];
      this.initialArticleContent.dynamicData.previewUrls = [];

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialArticleContent.languageCode = x.defaultLanguageCode;
        this.initialArticleContent.deliveryTargetCodes =
          x.defaultDeliveryTargetCodes ?? [];
        this.initialArticleContent.authors = x.defaultArticleAuthors ?? [];
        this._initContentListsForNewContent(x.defaultDeliveryTargetCodes);
        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Article';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewArticle) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewArticle = segments.length > 0 && segments.includes('new');
    });
  }

  markAsPristine() {
    this.initialArticleContent.body = this.form.get(FORM_PATHS.BODY()).value;
    this.form.markAsPristine();
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getArticleContent$(this.id));
        })
      )
      .pipe(
        catchError((error: IErrorResponse) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Article not found',
                message:
                  'The article specified was not found. It may have been deleted.',
              },
              'dashboard'
            );
          } else {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the article. Please contact IT.',
              },
              'dashboard'
            );
          }

          return of(null);
        })
      )
      .subscribe({
        next: (articleContentDto: IArticleContentDto) => {
          this.initialArticleContent = articleContentDto;

          this.form = this.buildForm();
          this.setEditMode(articleContentDto.dynamicData.canEdit);
          this._initForm();
          this._findEditionLanguage(this.getCurrentWebsites());
        },
      });
  }

  onAddToBody($event: ILinkInsert) {
    this.contentEditorComponent.insertContent($event);
  }

  // NOTE: Writes the outcome back onto the event object rather than returning it: an @Output
  // cannot return a value, and related-data needs the result in the same synchronous turn as the
  // accept that caused it, to say whether the fund was linked, skipped or not found.
  onAddPrivateMarketsFundBodyLink(
    $event: IPrivateMarketsFundBodyLinkAttempt
  ): void {
    $event.result =
      this.contentEditorComponent.insertPrivateMarketsFundBodyLink(
        $event.request
      );
  }

  /**
   * Shows the author every surface a suggestion's evidence was found in, then takes the tint away
   * again a few seconds later. Deliberately not gated on isEditMode — see ADR-0007.
   */
  onFlashMention($event: ISmartLinkMentionFlashTarget[]): void {
    this._clearMentionFlash();
    this._restartFieldFlash();

    const outcome = this._flashTargets($event);
    // NOTE: Raised even when another surface lit, and worded for the body alone — ADR-0007.
    if (outcome.bodyMiss) {
      this._snackBar.open(
        MENTION_FLASH_BODY_MISS_MESSAGES[outcome.bodyMiss],
        null,
        {
          duration: 5 * 1000,
        }
      );
    }
    if (outcome.lit.length === 0) {
      return;
    }

    this._bringFlashIntoView(outcome.lit);
    // NOTE: Nothing refreshes the flash, so the blocks it marked can be gone by the time this
    // fires — clearing is keyed off what still carries the attribute (ADR-0007).
    this.mentionFlashTimer = window.setTimeout(
      () => this._clearMentionFlash(),
      MENTION_FLASH_MS
    );
  }

  onAddBlockToBody($event: DataBlockType) {
    if (this.contentEditorComponent.insertBlock($event) === 'existing') {
      this._snackBar.open(
        'This story already has a Funds in this story block. It is now selected in the body.',
        null,
        { duration: 5 * 1000 }
      );
    }
  }

  onRemoveLink($event) {
    this.contentEditorComponent.replaceContent($event.regex, $event.newValue);
  }

  onTagItemSelected($event) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;

    handleTagSelected($event, typedTags);
  }

  onTagItemRemoved($event: ITypedSelectListItem) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagRemoved($event, typedTags, surtitleControl);
  }

  onSurtitlePromoted($event: ITypedSelectListItem) {
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagAddedAsSurtitle($event, surtitleControl);
  }

  private getArticleContent$(id: string) {
    if (this._isVersionId(id)) {
      this.articleContent$ = this.process$(
        this.articleService.getByVersionId$(id)
      );
    } else {
      this.articleContent$ = this.process$(this.articleService.getById$(id));
    }

    return this.articleContent$;
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.ARTICLE],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.ARTICLE],
      id,
      'edit',
    ]);
  }

  private _initForm(): void {
    this._initDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
    this._initHeadline(this.initialArticleContent.headline);
    this._initStandfirst(this.initialArticleContent.standfirst);
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initNextStates(
      this.initialArticleContent.dynamicData.allowableNextStates
    );
    this._initContentBody(
      this.initialArticleContent.body,
      this.initialArticleContent.bodyImages,
      this.initialArticleContent.bodyVideos
    );
    this._initPublishTime(
      this.initialArticleContent.presetPublishDate,
      this.initialArticleContent.timezoneIdentifier
    );

    this._initContentList(this.initialArticleContent.contentLists);

    this._initAuthors(this.initialArticleContent.authors);

    this._initColumnistContent(this.initialArticleContent.isColumnistContent);

    this._initTagging(
      this.initialArticleContent.tags,
      this.initialArticleContent.topics,
      this.initialArticleContent.relatedCompanies,
      this.initialArticleContent.relatedPeople,
      this.topicService
    );

    this._initSurtitles(
      this.initialArticleContent.surtitlesDetailed,
      this.initialArticleContent.tags,
      this.initialArticleContent.topics,
      this.initialArticleContent.relatedCompanies,
      this.initialArticleContent.relatedPeople,
      this.initialArticleContent.relatedManagers,
      this.initialArticleContent.relatedFunds,
      this.initialArticleContent.relatedSectors,
      this.initialArticleContent.relatedAssetClasses,
      this.initialArticleContent.relatedEtfs,
      this.initialArticleContent.relatedInvestmentTrusts,
      this.initialArticleContent.relatedShares
    );

    this._initSponsors(
      this.initialArticleContent.sponsor,
      this.initialArticleContent.isSponsored
    );

    this._initFormValueChanges();

    this._initContentListTracking();

    this._initRelatedData(
      this.initialArticleContent.relatedManagers,
      this.initialArticleContent.relatedFunds,
      this.initialArticleContent.relatedSectors,
      this.initialArticleContent.relatedAssetClasses,
      this.initialArticleContent.relatedEtfs,
      this.initialArticleContent.relatedInvestmentTrusts,
      this.initialArticleContent.relatedShares,
      this.initialArticleContent.relatedPrivateMarketsFunds
    );
    this._initExternalLinks(this.initialArticleContent.externalLinks);

    this._initContentVisibility(
      this.initialArticleContent.restrictToRegisteredUsers,
      this.initialArticleContent.notForGoogleNewsSitemaps,
      this.initialArticleContent.notForNewsFeeds,
      this.initialArticleContent.hideThisArticle,
      this.initialArticleContent.paywallPremiumContent,
      this.initialArticleContent.notForCompetitors
    );

    this._initRelatedArticles(this.initialArticleContent.relatedContents);
    this._initMainVideoImage(
      this.initialArticleContent.mainImage,
      this.initialArticleContent.mainVideo,
      this.initialArticleContent.displayImageOnWebsite
    );
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('Article');
  }

  private _initFormValueChanges(): void {
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initHeadlineValueChanges(this.getHeadline);
    this._initContentBodyValueChanges(
      this.getBody,
      this.getBodyImgs,
      this.getBodyVideos
    );
    this._initStandfirstValueChanges(this.getStandfirst);
    this._initNextStatesValueChanges(this.getNextStates);
    this._initPublishTimeValueChanges(
      this.getPresetPublishTime,
      this.getTimezone
    );
    this._initContentListValueChanges(this.getContentLists);

    this._initRelatedDataValueChanges(
      this.getRelatedManagers,
      this.getRelatedFunds,
      this.getRelatedSectors,
      this.getRelatedAssetClasses,
      this.getRelatedEtfs,
      this.getRelatedInvestmentTrusts,
      this.getRelatedShares,
      this.getRelatedPrivateMarketsFunds
    );

    this._initRelatedArticlesValueChanges(this.getRelatedArticles);
    this._initExternalLinksValueChanges(this.getExternalLinks);
    this._initTaggingValueChanges(
      this.getTags,
      this.getTopics,
      this.getCompanies,
      this.getPeople
    );
    this._initSurtitlesValueChanges(this.getSurtitles);
    this._initContentVisibilityValueChanges(
      this.getRestrictToRegisteredUsers,
      this.getNotForGoogleNewsSitemaps,
      this.getNotForNewsFeeds,
      this.getHideThisArticle,
      this.getPaywallPremiumContent,
      this.getNotForCompetitors
    );

    this._initSelectedSponsorValueChanges(
      this.getSelectedSponsor,
      this.getIsSponsored
    );
    this._initColumnistContentValueChanges(this.getIsColumnistContent);
    this._initSelectedAuthorsValueChanges(this.getSelectedAuthors);
    this._initMainImageVideoValueChanges(
      this.getMainImage,
      this.getMainVideo,
      this.getDisplayImageOnWebsite
    );
  }

  private _updateForms(): void {
    this._updateDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
  }

  getCurrentSelectedLanguage = () => {
    return this.initialArticleContent.languageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.initialArticleContent.deliveryTargetCodes;
  };

  getCurrentUrls = (): IWebsiteUrl[] | null => {
    if (this.initialArticleContent.status != ContentStatus.PUBLISHED) {
      return this.initialArticleContent.deliveryTargetCodes.map(
        (targetCode) => {
          return {
            deliveryTargetCode: targetCode,
            url: this.urlService.getPreviewUrl(
              this.initialArticleContent.id,
              targetCode
            ),
          };
        }
      );
    }
    return this.initialArticleContent.dynamicData.liveUrls;
  };

  getHeadline = (): string | null => {
    return this.initialArticleContent.headline;
  };

  getBody = (): string | null => {
    return this.initialArticleContent.body;
  };

  getBodyImgs = (): IImgMediaDto[] | null => {
    return this.initialArticleContent.bodyImages;
  };

  getBodyVideos = (): IVideoMediaDto[] | null => {
    return this.initialArticleContent.bodyVideos;
  };

  getStandfirst = (): string | null => {
    return this.initialArticleContent.standfirst;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialArticleContent.dynamicData.allowableNextStates;
  };

  getPresetPublishTime = (): Date | null => {
    return this.initialArticleContent.presetPublishDate;
  };

  getTimezone = (): string | null => {
    return this.initialArticleContent.timezoneIdentifier;
  };

  getContentLists = (): IContentListDto[] | null => {
    return this.initialArticleContent.contentLists;
  };

  getRelatedManagers = (): IManager[] | null => {
    return this.initialArticleContent.relatedManagers;
  };

  getRelatedFunds = (): IFund[] | null => {
    return this.initialArticleContent.relatedFunds;
  };

  getRelatedSectors = (): ISector[] | null => {
    return this.initialArticleContent.relatedSectors;
  };

  getRelatedAssetClasses = (): IAssetClass[] | null => {
    return this.initialArticleContent.relatedAssetClasses;
  };

  getRelatedEtfs = (): IEtf[] | null => {
    return this.initialArticleContent.relatedEtfs;
  };

  getRelatedInvestmentTrusts = (): IInvestmentTrust[] | null => {
    return this.initialArticleContent.relatedInvestmentTrusts;
  };

  getRelatedShares = (): IShare[] | null => {
    return this.initialArticleContent.relatedShares;
  };

  getRelatedPrivateMarketsFunds = (): IPrivateMarketsFund[] | null => {
    return this.initialArticleContent.relatedPrivateMarketsFunds ?? [];
  };
  getExternalLinks = (): IExternalLink[] | null => {
    return this.initialArticleContent.externalLinks;
  };

  getSurtitles = (): ISurtitle[] | null => {
    return this.initialArticleContent.surtitlesDetailed;
  };

  getTags = (): ITag[] | null => {
    return this.initialArticleContent.tags;
  };

  getTopics = (): ITopic[] | null => {
    return this.initialArticleContent.topics;
  };

  getCompanies = (): ICompany[] | null => {
    return this.initialArticleContent.relatedCompanies;
  };

  getPeople = (): IPerson[] | null => {
    return this.initialArticleContent.relatedPeople;
  };
  getRestrictToRegisteredUsers = (): boolean | null => {
    return this.initialArticleContent.restrictToRegisteredUsers;
  };
  getNotForGoogleNewsSitemaps = (): boolean | null => {
    return this.initialArticleContent.notForGoogleNewsSitemaps;
  };
  getNotForNewsFeeds = (): boolean | null => {
    return this.initialArticleContent.notForNewsFeeds;
  };
  getHideThisArticle = (): boolean | null => {
    return this.initialArticleContent.hideThisArticle;
  };
  getPaywallPremiumContent = (): boolean | null => {
    return this.initialArticleContent.paywallPremiumContent;
  };
  getNotForCompetitors = (): boolean | null => {
    return this.initialArticleContent.notForCompetitors;
  };

  getRelatedArticles = (): IRelatedContent[] | null => {
    return this.initialArticleContent.relatedContents;
  };

  getSelectedSponsor = (): ISponsor | null => {
    return this.initialArticleContent.sponsor;
  };

  getIsSponsored = (): boolean | null => {
    return this.initialArticleContent.isSponsored;
  };

  getIsColumnistContent = (): boolean | null => {
    return this.initialArticleContent.isColumnistContent;
  };

  getSelectedAuthors = (): IAuthor[] | null => {
    return this.initialArticleContent.authors;
  };
  getMainImage = (): IImgMediaDto | null => {
    return this.initialArticleContent.mainImage;
  };
  getMainVideo = (): IVideoMediaDto | null => {
    return this.initialArticleContent.mainVideo;
  };
  getDisplayImageOnWebsite = (): boolean => {
    return this.initialArticleContent.displayImageOnWebsite;
  };

  onCreateNewVersion(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .createNewVersionWithCurrentStateCheck$(
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialArticleContent.contentId,
        this.initialArticleContent.id,
        this.initialArticleContent.dynamicData.currentDraftVersion
      )
      .subscribe(() => {
        this._snackBar.open(`New article version created successfully`, null, {
          duration: 5 * 1000,
        });
        this.isProcessingStateChange = false;
        this.redirectToEditContent(this.initialArticleContent.contentId);
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    this.isProcessingStateChange = true;
    const updateStateFn = (initialArticleContentId: string, action: string) =>
      this.articleService.updateState$(initialArticleContentId, action);

    const setEditModeFn = (newEditModeValue: boolean) =>
      this.setEditMode(newEditModeValue);

    this.sharedService
      .updateState<IArticleContentDto>(
        updateStateFn,
        this.getContentFn$,
        setEditModeFn,
        this.initialArticleContent.id,
        stateAction,
        this.isEditMode
      )
      .pipe(
        tap((refreshedDto: IArticleContentDto) => {
          this.initialArticleContent = refreshedDto;
          this.markAsPristine();
        }),
        finalize(() => (this.isProcessingStateChange = false))
      )
      .subscribe();
  }

  onSave(): void {
    if (this.isNewArticle) {
      this.isSaving = true;
      const createFn$ = (content) => {
        return this.articleService.create$(content);
      };
      this.sharedService
        .createNewContent$<IArticleContentDto>(createFn$, this.buildArticle())
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((x: ISaveContentResponseDto) => {
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.ARTICLE],
            x.contentId,
            'edit',
          ]);
        });
    } else {
      this.saveExistingArticle$().subscribe();
    }
  }

  // Returned so callers can sequence work after the save (smart linking triggers a run once it
  // lands). Completes without emitting when a save dialog is cancelled.
  private saveExistingArticle$(): Observable<IArticleContentDto> {
    const updateFn$ = (id: string, content: IArticleContentDto) => {
      return this.articleService.update$(id, content);
    };
    const staleRecordFn = (content: IArticleContentDto) => {
      return this.handleStaleArticle(content);
    };

    this.isSaving = true;

    return this.sharedService
      .saveContent<IArticleContentDto>(
        updateFn$,
        this.getContentFn$,
        staleRecordFn,
        this.getCurrentWebsites(),
        this._buildDeliveryTargetCodes(),
        this.deliveryTargets$,
        this.buildArticle()
      )
      .pipe(
        finalize(() => (this.isSaving = false)),
        tap((refreshedDto: IArticleContentDto) => {
          this._snackBar.open('Article saved successfully', null, {
            duration: 5 * 1000,
          });
          this.initialArticleContent = refreshedDto;
          this.form.markAsPristine();
          this._updateForms();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        })
      );
  }

  private handleStaleArticle(newArticleContent: IArticleContentDto) {
    this._contentStateService.setContentDto(newArticleContent);
    this.form.markAsPristine();
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.ARTICLE],
      newArticleContent.contentId,
      'conflict',
    ]);
  }

  deleteArticle() {
    const deleteFn$ = (contentId: string) => {
      return this.articleService.delete$(contentId);
    };
    this.sharedService
      .deleteContent$(deleteFn$, this.initialArticleContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  spikeArticle() {
    const spikeFn$ = (contentId: string) => {
      return this.articleService.spike$(contentId);
    };

    this.sharedService
      .spikeContent$(spikeFn$, this.initialArticleContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  repurposeArticle() {
    const repurposeFn$ = (versionId: string) => {
      return this.articleService.repurpose$(versionId, ContentStatus.WRITING);
    };

    this.sharedService
      .repurposeContent$(repurposeFn$, this.initialArticleContent.id)
      .subscribe({
        next: (result: IRepurposeContentResponse) => {
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.ARTICLE.toLowerCase()],
            result.newArticleContentId,
            'edit',
          ]);
        },
      });
  }

  onReactivateArticle(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .reactivateContent(
        this.getContentFn$,
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialArticleContent.contentId,
        this.initialArticleContent.id,
        this.initialArticleContent.dynamicData.latestPublishedVersionId,
        this.getCurrentWebsites(),
        this.initialArticleContent.dynamicData.currentDraftVersion,
        this.deliveryTargets$,
        this.isPreviouslyPublishedVersion()
      )
      .pipe(finalize(() => (this.isProcessingStateChange = false)))
      .subscribe({
        next: () => {
          this._snackBar.open(`Article Reactivated successfully`, null, {
            duration: 5 * 1000,
          });
          this.redirectToEditContent(this.initialArticleContent.contentId);
        },
      });
  }

  private isPreviouslyPublishedVersion(): boolean {
    return (
      this.initialArticleContent.isPublished() &&
      this.initialArticleContent?.dynamicData?.latestPublishedVersionId !==
        this.initialArticleContent?.id
    );
  }

  deleteUnpublishedDraft() {
    this.sharedService
      .deleteUnpublishedDraft$(
        this.deleteVersionFn$,
        this.initialArticleContent.contentId,
        this.initialArticleContent.id
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  onEditionAdded(deliveryTargetCode: string): void {
    this._handleEditionAdded(deliveryTargetCode);
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  onEditionRemoved(deliveryTargetCode: string): void {
    this._handleEditionRemoved(deliveryTargetCode);
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  private buildArticle(): ArticleContentDto {
    const newArticleContent = new ArticleContentDto({});
    Object.assign(newArticleContent, this.initialArticleContent);
    newArticleContent.headline = this._buildHeadline();
    newArticleContent.body = this._buildBody();
    newArticleContent.bodyImages = this._buildBodyImages();
    newArticleContent.bodyVideos = this._buildBodyVideos();
    newArticleContent.deliveryTargetCodes = this._buildDeliveryTargetCodes();
    newArticleContent.languageCode = this._buildLanguage();
    newArticleContent.standfirst = this._buildStandfirst();
    newArticleContent.presetPublishDate = this._buildPresetPublishTime();
    newArticleContent.timezoneIdentifier = this._buildTimezone();
    newArticleContent.contentLists = this._buildContentList();
    newArticleContent.relatedManagers = this._buildRelatedManagers();
    newArticleContent.relatedFunds = this._buildRelatedFunds();
    newArticleContent.relatedSectors = this._buildRelatedSectors();
    newArticleContent.relatedAssetClasses = this._buildRelatedAssetClasses();
    newArticleContent.relatedEtfs = this._buildRelatedEtfs();
    newArticleContent.relatedInvestmentTrusts =
      this._buildRelatedInvestmentTrusts();
    newArticleContent.relatedShares = this._buildRelatedShares();
    newArticleContent.relatedPrivateMarketsFunds =
      this._buildRelatedPrivateMarketsFunds();
    newArticleContent.authors = this._buildSelectedAuthors();
    newArticleContent.relatedContents = this._buildRelatedArticles();
    newArticleContent.isColumnistContent = this._buildColumnistContent();
    newArticleContent.externalLinks = this._buildExternalLinks();
    newArticleContent.surtitles = [];
    newArticleContent.surtitlesDetailed = this._buildSurtitles();
    newArticleContent.topics = this._buildTopics();
    newArticleContent.tags = this._buildTags();
    newArticleContent.relatedCompanies = this._buildCompanies();
    newArticleContent.relatedPeople = this._buildPeople();
    newArticleContent.restrictToRegisteredUsers =
      this._buildRestrictToRegisteredUsers();
    newArticleContent.notForGoogleNewsSitemaps =
      this._buildNotForGoogleNewsSitemaps();
    newArticleContent.notForNewsFeeds = this._buildNotForNewsFeeds();
    newArticleContent.hideThisArticle = this._buildHideThisArticle();
    newArticleContent.paywallPremiumContent =
      this._buildPaywallPremiumContent();
    newArticleContent.notForCompetitors = this._buildNotForCompetitors();
    newArticleContent.sponsor = this._buildSelectedSponsor();
    newArticleContent.isSponsored = this._buildIsSponsored();
    newArticleContent.mainImage = this._buildMainImage();
    newArticleContent.mainVideo = this._buildMainVideo();
    newArticleContent.displayImageOnWebsite =
      this._buildDisplayImageOnWebsite();

    return newArticleContent;
  }

  /**
   * Lights every target that still resolves, recording which surfaces took a tint and why a body
   * target came back empty. Only the body can: a field is there whether its clause still is.
   *
   * NOTE: At most one body target arrives per click — evidence rows are grouped by field — which
   * is what lets flashMentions() be called inside a loop, since it clears the blocks the previous
   * call marked before marking its own.
   */
  private _flashTargets(
    targets: ISmartLinkMentionFlashTarget[]
  ): IMentionFlashOutcome {
    const lit: SmartLinkEvidenceField[] = [];
    const fields: SmartLinkEvidenceField[] = [];
    let bodyMiss: ClauseMissReason | null = null;

    targets.forEach((target) => {
      if (target.field !== 'body') {
        fields.push(target.field);
        lit.push(target.field);
        return;
      }
      const blocks =
        this.contentEditorComponent?.flashMentions(target.clauses) ?? 0;
      if (blocks === 0) {
        bodyMiss =
          this.contentEditorComponent?.explainMentionMiss(target.clauses) ??
          'absent';
        return;
      }
      lit.push(target.field);
    });

    this.flashedFields = fields;
    return { lit, bodyMiss };
  }

  // NOTE: The class must leave the DOM before the layout read — cleared and re-set in one
  // handler, change detection sees no change and a repeat click never re-flashes (ADR-0007).
  private _restartFieldFlash(): void {
    this._changeDetectorRef.detectChanges();
    this._forceReflow();
  }

  private _forceReflow(): number {
    return window.document.body.offsetHeight;
  }

  // NOTE: The feature's only scrollIntoView, and outside the iframe — called on a block inside
  // one it would scroll this page too, which is what the AC asks not to happen.
  private _bringFlashIntoView(lit: SmartLinkEvidenceField[]): void {
    const topmost = FLASH_SURFACE_ORDER.find((field) => lit.includes(field));
    const host = this._flashSurfaceHost(topmost);
    if (
      !host ||
      !shouldBringEditorIntoView(host.getBoundingClientRect(), window.innerHeight)
    ) {
      return;
    }
    host.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private _flashSurfaceHost(
    field: SmartLinkEvidenceField | undefined
  ): HTMLElement | null {
    if (field === 'headline') {
      return this.headlineHost?.nativeElement ?? null;
    }
    if (field === 'standfirst') {
      return this.standfirstHost?.nativeElement ?? null;
    }
    if (field === 'body') {
      return this.contentEditorHost?.nativeElement ?? null;
    }
    return null;
  }

  private _clearMentionFlash(): void {
    window.clearTimeout(this.mentionFlashTimer);
    this.mentionFlashTimer = null;
    this.flashedFields = [];
    this.contentEditorComponent?.clearMentionFlash();
  }

  ngOnDestroy(): void {
    this._clearMentionFlash();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
